/**
 * 🎨 Chat Widget - Interfaz de usuario del chat
 * @version 1.0.0
 * @description Widget de chat para la interfaz de usuario
 */

import { ThemeManager } from "./theme-manager.js";
import { EventManager } from "../core/event-manager.js";
import { MessageProcessor } from "../core/message-processor.js";

export class ChatWidget {
  constructor(config = {}) {
    this.config = {
      title: "💬 Chat",
      defaultOptions: [],
      theme: "professional",
      debug: false,
      ...config,
    };

    // Componentes
    this.themeManager = new ThemeManager();
    this.eventManager = new EventManager();
    this.messageProcessor = new MessageProcessor({ debug: this.config.debug });

    // Estado del widget
    this.chatOpen = false;
    this.isTyping = false;
    this.welcomeMessage = null;
    this.personalityName = null;

    // Referencias del DOM
    this.chatWidget = null;
    this.chatWindow = null;
    this.chatBody = null;
    this.chatInput = null;
    this.chatButton = null;
    this.chatCloseButton = null;
    this.chatForm = null;
    this.chatOptionsElement = null;
    this.welcomeMessageElement = null;

    // Callbacks
    this.onMessageSend = null;
    this.onOptionSelect = null;
  }

  /**
   * 🎨 Crea el widget de chat
   * @param {Function} onMessageSend - Callback para envío de mensajes
   * @param {Function} onOptionSelect - Callback para selección de opciones
   */
  create(onMessageSend, onOptionSelect) {
    if (this.chatWidget) return; // Ya existe

    this.onMessageSend = onMessageSend;
    this.onOptionSelect = onOptionSelect;

    // Inyectar estilos CSS
    this.themeManager.injectStyles(this.config.theme);

    // Crear estructura HTML
    this._createHTML();

    // Configurar event listeners
    this._setupEventListeners();

    if (this.config.debug) {
      console.log("🎨 [Chat Widget] Widget creado");
    }
  }

  /**
   * 🗑️ Destruye el widget y limpia recursos
   */
  destroy() {
    // Remover event listeners
    this.eventManager.removeAllListeners();

    // Remover widget del DOM
    if (this.chatWidget && this.chatWidget.parentNode) {
      this.chatWidget.parentNode.removeChild(this.chatWidget);
    }

    // Remover estilos CSS
    this.themeManager.removeStyles();

    // Limpiar referencias del DOM
    this._clearDOMReferences();

    // Resetear estado
    this.chatOpen = false;
    this.isTyping = false;
    this.welcomeMessage = null;
    this.personalityName = null;

    if (this.config.debug) {
      console.log("🗑️ [Chat Widget] Widget destruido");
    }
  }

  /**
   * 🔄 Toggle del chat
   */
  toggleChat() {
    this.chatOpen = !this.chatOpen;

    if (this.chatOpen) {
      this.chatWindow.classList.add("open");
      this.chatButton.classList.add("active");
      this.chatButton.innerHTML = "✕";
      this.chatButton.title = "Cerrar chat";
      this.chatInput.focus();
    } else {
      this.chatWindow.classList.remove("open");
      this.chatButton.classList.remove("active");
      this.chatButton.innerHTML = "💬";
      this.chatButton.title = "Abrir chat";
    }

    if (this.config.debug) {
      console.log(
        `🔄 [Chat Widget] Chat ${this.chatOpen ? "abierto" : "cerrado"}`
      );
    }
  }

  /**
   * 💬 Agrega mensaje del usuario
   * @param {string} message - Mensaje del usuario
   */
  addUserMessage(message) {
    const messageDiv = document.createElement("div");
    messageDiv.className = "modul-assistant-sdk-chat-message user";
    messageDiv.textContent = message;
    this.chatBody.appendChild(messageDiv);
    this._scrollToBottom();
  }

  /**
   * 🤖 Agrega mensaje del bot
   * @param {string} message - Mensaje del bot
   */
  addBotMessage(message) {
    const messageDiv = document.createElement("div");
    messageDiv.className = "modul-assistant-sdk-chat-message bot";

    // Parsear y renderizar markdown si es necesario
    if (this.messageProcessor.isMarkdown(message)) {
      messageDiv.innerHTML = this.messageProcessor.parseMarkdown(message);
    } else {
      messageDiv.textContent = message;
    }

    this.chatBody.appendChild(messageDiv);
    this._scrollToBottom();
  }

  /**
   * ⌨️ Muestra indicador de escritura
   */
  showTypingIndicator() {
    if (this.isTyping) return;

    const typingDiv = document.createElement("div");
    typingDiv.className = "modul-assistant-sdk-typing-indicator";
    typingDiv.id = "modul-assistant-sdk-typing-indicator";
    typingDiv.innerHTML = `
      <div class="modul-assistant-sdk-typing-dots">
        <div class="modul-assistant-sdk-typing-dot"></div>
        <div class="modul-assistant-sdk-typing-dot"></div>
        <div class="modul-assistant-sdk-typing-dot"></div>
      </div>
      <span>Escribiendo...</span>
    `;
    this.chatBody.appendChild(typingDiv);
    this._scrollToBottom();
    this.isTyping = true;
  }

  /**
   * 🛑 Oculta indicador de escritura
   */
  hideTypingIndicator() {
    const typingIndicator = document.getElementById(
      "modul-assistant-sdk-typing-indicator"
    );
    if (typingIndicator) {
      typingIndicator.remove();
    }
    this.isTyping = false;
  }

  /**
   * 📝 Actualiza el mensaje de bienvenida
   * @param {string} message - Nuevo mensaje de bienvenida
   */
  updateWelcomeMessage(message) {
    this.welcomeMessage = message;
    if (this.welcomeMessageElement && message) {
      // Parsear markdown si es necesario para el mensaje de bienvenida
      if (this.messageProcessor.isMarkdown(message)) {
        this.welcomeMessageElement.innerHTML =
          this.messageProcessor.parseMarkdown(message);
      } else {
        this.welcomeMessageElement.textContent = message;
      }

      if (this.config.debug) {
        console.log(
          "📝 [Chat Widget] Mensaje de bienvenida actualizado:",
          message
        );
      }
    }
  }

  /**
   * 🎯 Actualiza las opciones por defecto
   * @param {Array} options - Array de opciones
   */
  updateDefaultOptions(options) {
    this.config.defaultOptions = options;
    if (this.chatOptionsElement && options && options.length > 0) {
      let optionsHTML = "";

      options.forEach((option) => {
        if (option.text && option.value) {
          optionsHTML += `<button class="modul-assistant-sdk-chat-option" data-option="${option.value}">${option.text}</button>`;
        }
      });

      this.chatOptionsElement.innerHTML = optionsHTML;
      this.chatOptionsElement.style.display = "flex";

      if (this.config.debug) {
        console.log(
          "🎯 [Chat Widget] Opciones por defecto actualizadas:",
          options
        );
      }
    }
  }

  /**
   * ❌ Muestra un mensaje de error de inicialización
   * @param {string} errorMessage - Mensaje de error
   */
  showInitializationError(errorMessage) {
    if (this.welcomeMessageElement) {
      // Crear mensaje de error más amigable para el usuario
      const userFriendlyError =
        this.messageProcessor.createUserFriendlyErrorMessage(
          errorMessage,
          this.config.debug
        );

      // Actualizar el elemento del mensaje de bienvenida con el error
      this.welcomeMessageElement.innerHTML = userFriendlyError;
      this.welcomeMessageElement.classList.add("error-message");

      // Ocultar las opciones por defecto ya que hay un error
      if (this.chatOptionsElement) {
        this.chatOptionsElement.style.display = "none";
      }

      if (this.config.debug) {
        console.log(
          "❌ [Chat Widget] Mensaje de error mostrado:",
          errorMessage
        );
      }
    }
  }

  /**
   * 🎨 Cambia el tema del widget
   * @param {string} newTheme - Nuevo tema
   */
  changeTheme(newTheme) {
    this.config.theme = newTheme;
    this.themeManager.changeTheme(newTheme);
  }

  /**
   * 🔄 Limpia el input de mensaje
   */
  clearInput() {
    if (this.chatInput) {
      this.chatInput.value = "";
    }
  }

  /**
   * 👁️ Oculta las opciones por defecto
   */
  hideDefaultOptions() {
    if (this.chatOptionsElement) {
      this.chatOptionsElement.style.display = "none";
    }
  }

  /**
   * 📊 Obtiene información del widget
   * @returns {Object} Información del widget
   */
  getInfo() {
    return {
      isOpen: this.chatOpen,
      isTyping: this.isTyping,
      theme: this.config.theme,
      hasWelcomeMessage: !!this.welcomeMessage,
      optionsCount: this.config.defaultOptions.length,
      elementsCreated: !!this.chatWidget,
    };
  }

  /**
   * 🏗️ Crea la estructura HTML del chat
   * @private
   */
  _createHTML() {
    // Crear widget principal
    this.chatWidget = document.createElement("div");
    this.chatWidget.className = "modul-assistant-sdk-chat-widget";

    // Botón del chat
    this.chatButton = document.createElement("button");
    this.chatButton.className = "modul-assistant-sdk-chat-button";
    this.chatButton.innerHTML = "💬";
    this.chatButton.title = "Abrir chat";

    // Ventana del chat
    this.chatWindow = document.createElement("div");
    this.chatWindow.className = "modul-assistant-sdk-chat-window";

    // Header
    const chatHeader = document.createElement("div");
    chatHeader.className = "modul-assistant-sdk-chat-header";
    chatHeader.innerHTML = `
      <h4>${this.config.title}</h4>
      <button class="modul-assistant-sdk-chat-close">✕</button>
    `;

    // Body
    this.chatBody = document.createElement("div");
    this.chatBody.className = "modul-assistant-sdk-chat-body";

    // Mensaje de bienvenida (se actualizará después de la inicialización)
    this.welcomeMessageElement = document.createElement("div");
    this.welcomeMessageElement.className =
      "modul-assistant-sdk-chat-message bot";
    this.welcomeMessageElement.textContent =
      "¡Hola! 👋 Soy el asistente virtual. ¿En qué puedo ayudarte hoy?";

    // Opciones rápidas
    this.chatOptionsElement = document.createElement("div");
    this.chatOptionsElement.className = "modul-assistant-sdk-chat-options";
    this.chatOptionsElement.innerHTML = "";

    this.chatBody.appendChild(this.welcomeMessageElement);
    this.chatBody.appendChild(this.chatOptionsElement);

    // Input area
    const chatInputArea = document.createElement("div");
    chatInputArea.className = "modul-assistant-sdk-chat-input-area";

    this.chatForm = document.createElement("form");
    this.chatForm.className = "modul-assistant-sdk-chat-input-form";

    this.chatInput = document.createElement("input");
    this.chatInput.type = "text";
    this.chatInput.className = "modul-assistant-sdk-chat-input";
    this.chatInput.placeholder = "Escribe tu mensaje...";

    const chatSend = document.createElement("button");
    chatSend.type = "submit";
    chatSend.className = "modul-assistant-sdk-chat-send";
    chatSend.innerHTML = "📤";

    this.chatForm.appendChild(this.chatInput);
    this.chatForm.appendChild(chatSend);
    chatInputArea.appendChild(this.chatForm);

    // Ensamblar todo
    this.chatWindow.appendChild(chatHeader);
    this.chatWindow.appendChild(this.chatBody);
    this.chatWindow.appendChild(chatInputArea);

    this.chatWidget.appendChild(this.chatButton);
    this.chatWidget.appendChild(this.chatWindow);

    // Agregar al DOM
    document.body.appendChild(this.chatWidget);

    // Guardar referencias
    this.chatCloseButton = chatHeader.querySelector(
      ".modul-assistant-sdk-chat-close"
    );
  }

  /**
   * 🎯 Configura los event listeners
   * @private
   */
  _setupEventListeners() {
    const elements = {
      chatButton: this.chatButton,
      chatCloseButton: this.chatCloseButton,
      chatForm: this.chatForm,
      chatOptionsElement: this.chatOptionsElement,
    };

    const callbacks = {
      toggleChat: () => this.toggleChat(),
      handleMessageSubmit: (e) => this._handleMessageSubmit(e),
      handleOptionClick: (e) => this._handleOptionClick(e),
    };

    this.eventManager.setupEventListeners(elements, callbacks);
  }

  /**
   * 📝 Maneja el envío de mensajes
   * @private
   */
  _handleMessageSubmit(e) {
    e.preventDefault();
    const message = this.chatInput.value.trim();

    if (!message) return;

    // Agregar mensaje del usuario
    this.addUserMessage(message);
    this.clearInput();

    // Ocultar opciones después del primer mensaje
    this.hideDefaultOptions();

    // Llamar callback externo
    if (this.onMessageSend) {
      this.onMessageSend(message);
    }
  }

  /**
   * 🎯 Maneja clicks en opciones rápidas
   * @private
   */
  _handleOptionClick(e) {
    if (e.target.classList.contains("modul-assistant-sdk-chat-option")) {
      const option = e.target.dataset.option;
      const optionText = e.target.textContent.trim();

      // Agregar como mensaje del usuario
      this.addUserMessage(optionText);

      // Ocultar opciones
      this.hideDefaultOptions();

      // Llamar callback externo
      if (this.onOptionSelect) {
        this.onOptionSelect(option, optionText);
      }
    }
  }

  /**
   * 📜 Scroll automático al final
   * @private
   */
  _scrollToBottom() {
    this.chatBody.scrollTop = this.chatBody.scrollHeight;
  }

  /**
   * 🧹 Limpia todas las referencias del DOM
   * @private
   */
  _clearDOMReferences() {
    this.chatWidget = null;
    this.chatWindow = null;
    this.chatBody = null;
    this.chatInput = null;
    this.chatButton = null;
    this.chatCloseButton = null;
    this.chatForm = null;
    this.chatOptionsElement = null;
    this.welcomeMessageElement = null;
  }
}
